import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../../shared/api/client';
import type {
  AtualizarColaboradorInput,
  ProjetoColaboradorVinculado,
  VincularColaboradorInput,
} from './types';

/**
 * Vínculo Colaborador↔Projeto (transaction `ColaboradorClienteProjeto`), a
 * partir do projeto. Espelha `requisicoes.api.ts` (associação de RC).
 * Card [1pns0M7e]/ATD-0050. `POST` grava `DataInicio = Today()`.
 */
const base = (clientId: number, projectId: number): string =>
  `/v1/clients/${clientId}/projects/${projectId}/colaboradores`;

export async function listProjetoColaboradores(
  clientId: number,
  projectId: number,
): Promise<ProjetoColaboradorVinculado[]> {
  const { data } = await api.get<{ data: ProjetoColaboradorVinculado[] }>(
    base(clientId, projectId),
  );
  return data.data;
}

export async function linkProjetoColaborador(
  clientId: number,
  projectId: number,
  input: VincularColaboradorInput,
): Promise<void> {
  await api.post(base(clientId, projectId), {
    colaboradorCodigo: input.colaboradorCodigo,
    dataInicio: input.dataInicio,
    dataTermino: input.dataTermino ?? null,
  });
}

export async function unlinkProjetoColaborador(
  clientId: number,
  projectId: number,
  colaboradorCodigo: number,
): Promise<void> {
  await api.delete(`${base(clientId, projectId)}/${colaboradorCodigo}`);
}

export async function updateProjetoColaborador(
  clientId: number,
  projectId: number,
  input: AtualizarColaboradorInput,
): Promise<void> {
  await api.put(`${base(clientId, projectId)}/${input.colaboradorCodigo}`, {
    dataInicio: input.dataInicio,
    dataTermino: input.dataTermino ?? null,
  });
}

const queryKey = (clientId: number, projectId: number): readonly unknown[] => [
  'client-projects',
  clientId,
  projectId,
  'colaboradores',
];

export function useProjetoColaboradores(
  clientId: number,
  projectId: number | null,
): UseQueryResult<ProjetoColaboradorVinculado[]> {
  return useQuery({
    queryKey: queryKey(clientId, projectId ?? 0),
    queryFn: () => listProjetoColaboradores(clientId, projectId as number),
    enabled: projectId !== null && projectId > 0,
    staleTime: 15_000,
  });
}

export function useLinkProjetoColaborador(
  clientId: number,
  projectId: number,
): UseMutationResult<void, Error, VincularColaboradorInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => linkProjetoColaborador(clientId, projectId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKey(clientId, projectId) }),
  });
}

export function useUnlinkProjetoColaborador(
  clientId: number,
  projectId: number,
): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (codigo) => unlinkProjetoColaborador(clientId, projectId, codigo),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKey(clientId, projectId) }),
  });
}

export function useUpdateProjetoColaborador(
  clientId: number,
  projectId: number,
): UseMutationResult<void, Error, AtualizarColaboradorInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => updateProjetoColaborador(clientId, projectId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKey(clientId, projectId) }),
  });
}
